import { useEffect, useState, memo, useRef } from "react";
import { getCheckoutOffers } from "@/api/home";
import { ShimmerPostList, ShimmerTitle } from "react-shimmer-effects";
import Slider from "react-slick";
import { sliderSettingOffers } from "./data";
import { getEventCategory, trackButtonClick } from "@/utils/analytics";
import { markInternalNavigation } from "@/utils/attributionCapture";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import "./CheckoutOffers.css";

const CheckoutOffers = ({ offerCmsData }) => {
  const sliderRef = useRef(null);
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [slideCount, setSlideCount] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const settings = sliderSettingOffers;

  const getCMSOffers = () => {
    const offerListName = offerCmsData?.checkoutOfferList["@nodes"];
    return offerListName?.map((item) => {
      return {
        ...offerCmsData?.checkoutOfferList[item],
        id: offerCmsData?.checkoutOfferList[item]["@id"],
      };
    });
  };

  const getOffers = async () => {
    try {
      setIsLoading(true);
      const CCDashboardOffers = await getCheckoutOffers();
      const CMSOfferList = await getCMSOffers();
      let allOffers = [];
      if (CCDashboardOffers) {
        allOffers = [...allOffers, ...CCDashboardOffers]
          .filter((i) => i?.enabled !== "N")
          .filter((i) => i?.campaignImage?.url);
      }
      if (CMSOfferList) {
        allOffers = [...allOffers, ...CMSOfferList].filter(
          (i) => i?.enabled !== "N"
        );
      }
      const fullBannerIndex = [0];
      let tempNum = 0;
      [...Array(allOffers.length).keys()].forEach((num) => {
        if (num > tempNum + 2) {
          fullBannerIndex.push(num);
          tempNum = num;
        }
      });
      setOffers(
        allOffers
          .filter((i) => i?.campaignImage?.url || i?.imageURL)
          .map((item, index) => ({
            id: index,
            banner: item,
            isFull: true, // not used now, but safe
          }))
      );
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (offerCmsData?.checkoutOfferList) {
      getOffers();
    }
  }, [offerCmsData?.checkoutOfferList]);

  const LoaderComponent = () => {
    return (
      <section id="property-city" className="home-container pt60 pb0">
        <div className="home-title">
          <ShimmerTitle line={1} gap={20} variant="primary" />
        </div>
        <ShimmerPostList postStyle="STYLE_FOUR" col={4} row={1} gap={20} />
      </section>
    );
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
      trackButtonClick({
        pageName: "checkout_offers_slider_nav",
        eventCategory: getEventCategory(),
        eventLabel: "Section position: 4",
        eventParams: { direction: "right" },
      });
    }
  };

  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
      trackButtonClick({
        pageName: "checkout_offers_slider_nav",
        eventCategory: getEventCategory(),
        eventLabel: "Section position: 4",
        eventParams: { direction: "left" },
      });
    }
  };

  const customSettings = {
    ...settings,
    slidesToShow: 5,
    slidesToScroll: 5,
    arrows: false,
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 5,
        },
      },
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
        },
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 960,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
    beforeChange: (current, next) => setCurrentSlide(next),
    afterChange: (current) => {
      setCurrentSlide(current)
      // Handle accessibility after slide change
      setTimeout(() => {
        const carouselContainer = document.querySelector('.checkout-offers-slider');
        if (!carouselContainer) return;

        const hiddenSlides = carouselContainer.querySelectorAll('.slick-slide[aria-hidden="true"]');
        const visibleSlides = carouselContainer.querySelectorAll('.slick-slide:not([aria-hidden="true"])');

        hiddenSlides.forEach((slide) => {
          const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex="0"]');
          focusableElements.forEach((el) => {
            el.setAttribute('tabindex', '-1');
            el.setAttribute('aria-hidden', 'true');
          });
        });

        visibleSlides.forEach((slide) => {
          const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex="-1"]');
          focusableElements.forEach((el) => {
            el.removeAttribute('tabindex');
            el.removeAttribute('aria-hidden');
          });
        });
      }, 50);
    },
    onReInit: () => {
      if (sliderRef.current) {
        setSlideCount(offers.length);
      }
    },
    onInit: () => {
      if (offers.length) {
        setSlideCount(offers.length);
      }
    },
  };

  // Add this function to get current slidesToShow based on screen size
  const getCurrentSlidesToShow = () => {
    const width = window.innerWidth;
     if (width <= 600) return 1;
  if (width <= 960) return 3;
  if (width <= 1200) return 3;
  if (width <= 1400) return 4;
  if (width <= 1600) return 5;
    return 5;
  };

  useEffect(() => {
    if (offers.length) {
      setSlideCount(offers.length);
    }

    // Handle initial accessibility state
    const handleSlideAccessibility = () => {
      const carouselContainer = document.querySelector(
        ".checkout-offers-slider",
      );
      if (!carouselContainer) return;

      const hiddenSlides = carouselContainer.querySelectorAll(
        '.slick-slide[aria-hidden="true"]',
      );
      const visibleSlides = carouselContainer.querySelectorAll(
        '.slick-slide:not([aria-hidden="true"])',
      );

      hiddenSlides.forEach((slide) => {
        const focusableElements = slide.querySelectorAll(
          'a, button, input, select, textarea, [tabindex="0"]',
        );
        focusableElements.forEach((el) => {
          el.setAttribute("tabindex", "-1");
          el.setAttribute("aria-hidden", "true");
        });
      });

      visibleSlides.forEach((slide) => {
        const focusableElements = slide.querySelectorAll(
          'a, button, input, select, textarea, [tabindex="-1"]',
        );
        focusableElements.forEach((el) => {
          el.removeAttribute("tabindex");
          el.removeAttribute("aria-hidden");
        });
      });
    };

    const timer = setTimeout(handleSlideAccessibility, 100);
    return () => clearTimeout(timer);
  }, [offers]);

  return (
    <>
      {isLoading ? <LoaderComponent /> : <></>}
      {!isLoading && offers.length > 0 && (
        <section className="container home-container">
          <div className="checkout-offers-header">
            <h2 className="checkout-offers-title">
              {offerCmsData?.offerTitle}
            </h2>
            <div className="checkout-offers-arrows">
              <button
                id="checkout-offers-prev-btn"
                onClick={goToPrev}
                aria-label="goToPrev"
                className={`checkout-arrow prev ${
                  currentSlide === 0 ? "disabled" : ""
                }`}
                disabled={currentSlide === 0}
              >
                {currentSlide === 0 ? (
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 25L9 17L17 9" stroke="#ADC4DA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 17L25 17" stroke="#ADC4DA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="33.25" y="33.25" width="32.5" height="32.5" rx="3.25" transform="rotate(180 33.25 33.25)" stroke="#ADC4DA" strokeWidth="1.5"/>
                  </svg>
                ) : (
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" style={{transform: 'rotate(180deg)'}}>
                    <path d="M17 9L25 17L17 25" stroke="#118BF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M25 17H9" stroke="#118BF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="0.75" y="0.75" width="32.5" height="32.5" rx="3.25" stroke="#118BF4" strokeWidth="1.5"/>
                  </svg>
                )}
              </button>
              <button
                id="checkout-offers-next-btn"
                onClick={goToNext}
                aria-label="goToNext"
                className={`checkout-arrow next ${
                  currentSlide >= slideCount - getCurrentSlidesToShow()
                    ? "disabled"
                    : ""
                }`}
                disabled={currentSlide >= slideCount - getCurrentSlidesToShow()}
              >
                {currentSlide >= slideCount - getCurrentSlidesToShow() ? (
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 9L25 17L17 25" stroke="#ADC4DA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M25 17H9" stroke="#ADC4DA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="0.75" y="0.75" width="32.5" height="32.5" rx="3.25" stroke="#ADC4DA" strokeWidth="1.5"/>
                  </svg>
                ) : (
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 9L25 17L17 25" stroke="#118BF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M25 17H9" stroke="#118BF4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="0.75" y="0.75" width="32.5" height="32.5" rx="3.25" stroke="#118BF4" strokeWidth="1.5"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="checkout-offers-slider">
            <Slider ref={sliderRef} {...customSettings}>
              {offers.map((item, index) => {
                const banner = item.banner;
                const isHovered = hoveredIndex === index;

                return (
                  <div
                    key={`offer-${item.id}`}
                    id={`checkout-offer-slide-${index}`}
                    className="checkout-offer-slide"
                    onMouseEnter={() => {
                      setHoveredIndex(index);
                    }}
                    onMouseLeave={() => {
                      setHoveredIndex(null);
                    }}
                  >
                    <a
                      id={`checkout-offer-card-${index}`}
                      href={
                        banner?.campaignUrl === "CASH-INCENTIVE"
                          ? "/cash-incentive"
                          : banner?.url || banner?.newSlideLink || "/"
                      }
                      target={banner?.target || "_blank"}
                      className="checkout-offer-card"
                      onClick={() => {
                        markInternalNavigation();
                        trackButtonClick({
                          eventName: "promotion_click",
                          eventCategory: "Home Page 2.0",
                          eventSource: `Promotion Click - ${
                            banner?.campaignUrl === "CASH-INCENTIVE"
                              ? "/cash-incentive"
                              : banner?.newSlideLink
                          }`,
                        });
                      }}
                    >
                      <div className="checkout-offer-image">
                        <img
                          src={

                            banner?.campaignImage?.url ||
                            banner?.imageURL
                          }
                          alt={banner?.imageCaptionTitle || "offer"}
                        />
                      </div>
                      <div className="checkout-offer-content">
                        <h3 className="checkout-offer-title">
                          {banner?.campaignImage?.caption ||
                            banner?.imageCaptionTitle}
                        </h3>
                        <p className="checkout-offer-description">
                          {banner?.campaignImage?.captionText ||
                            banner?.imageCaptionText}
                        </p>
                        {isHovered && (
                          <a
                            id={`checkout-offer-btn-${index}`}
                            href={
                              banner?.campaignUrl === "CASH-INCENTIVE"
                                ? "/cash-incentive"
                                : banner?.url || banner?.newSlideLink || "/"
                            }
                            target={banner?.target || "_blank"}
                            className="checkout-offer-btn"
                            onClick={() => {
                              markInternalNavigation();
                              trackButtonClick({
                                eventName: "promotion_click",
                                eventCategory: "Home Page 2.0",
                                eventSource: `Promotion Click - ${
                                  banner?.campaignUrl === "CASH-INCENTIVE"
                                    ? "/cash-incentive"
                                    : banner?.newSlideLink
                                }`,
                              });
                            }}
                          >
                            Browse Properties
                          </a>
                        )}
                      </div>
                    </a>
                  </div>
                );
              })}
            </Slider>
          </div>
        </section>
      )}
    </>
  );
};

export default memo(CheckoutOffers);
